/**
 * useDailyInsights React Hook
 * 
 * Manages daily insights state, caching, and UI interactions.
 * Uses React Query for data fetching and localStorage for caching.
 */

'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useQuery } from '@tanstack/react-query';
import { DailyInsight, InsightsCache, UseDailyInsightsState } from '@/types/insights';
import { createInsightsClient } from '@/lib/api/insights';
import { markInsightAsViewed } from '@/lib/insights-storage';
import { STALE_TIME } from '@/lib/query-config';

const CACHE_KEY_PREFIX = 'daily_insights_cache';
// MIGRATION: Remove stale key set by older code that didn't verify insightId
const STALE_HAS_INSIGHTS_KEY = 'hasEverHadInsights';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Switching the active org (ProfileButton.tsx) is a client-side navigation —
// the app never unmounts, so both the localStorage cache below and React
// Query's in-memory cache must be keyed by org, not just by user, or a user
// with multiple org memberships sees the previous org's insight after
// switching (GitHub #132-class bug, reported against the Home page KPIs and
// insights specifically).
function cacheKey(orgId: string | null | undefined): string {
  return `${CACHE_KEY_PREFIX}_${orgId ?? 'unknown'}`;
}

/**
 * Prefix shared by every `dailyInsightsTodayQueryKey(...)` variant. Invalidate
 * with this (rather than reconstructing the full key) when something the Home
 * page's insight card is derived from changes outside this hook — e.g. an
 * Action Queue task the CSM just completed, whose row the backend now drops
 * from the tiers at read time (GitHub #833). Invalidating by prefix also
 * covers the admin viewAll=true variant without having to know which one the
 * card is currently rendering.
 */
export const dailyInsightsTodayQueryKeyPrefix = ['daily-insights', 'today'] as const;

export const dailyInsightsTodayQueryKey = (
  userId: string | null | undefined,
  orgId: string | null | undefined,
  viewAll: boolean
) => [...dailyInsightsTodayQueryKeyPrefix, userId, orgId, viewAll] as const;

export const insightsHasAnyQueryKey = (userId: string | null | undefined, orgId: string | null | undefined) =>
  ['insights-has-any', userId, orgId] as const;

/**
 * Get today's date key in YYYY-MM-DD format. Exported so callers outside
 * this hook (e.g. TodayInsightsCard.tsx's history-nav filtering) can compare
 * against "today" without duplicating this format.
 */
export function getTodayDateKey(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

/**
 * Load insights cache from localStorage
 */
function loadCache(orgId: string | null | undefined): InsightsCache | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = localStorage.getItem(cacheKey(orgId));
    if (!cached) return null;

    const cache: InsightsCache = JSON.parse(cached);

    // Check if cache is still valid (within TTL and same date). A 'generating'
    // placeholder is never a valid cache hit — seeding React Query's initialData
    // with it would freeze the UI on a blank insight until the tab reloads,
    // since refetchInterval only polls off live query data, not this cache.
    const isValid =
      cache.dateKey === getTodayDateKey() &&
      Date.now() - cache.timestamp < CACHE_TTL &&
      cache.insight?.status !== 'generating';

    return isValid ? cache : null;
  } catch (error) {
    console.error('[useDailyInsights] Error loading cache:', error);
    return null;
  }
}

/**
 * Save insights cache to localStorage
 */
function saveCache(orgId: string | null | undefined, insight: DailyInsight | null, seen: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    const cache: InsightsCache = {
      dateKey: getTodayDateKey(),
      timestamp: Date.now(),
      insight,
      seen,
    };

    localStorage.setItem(cacheKey(orgId), JSON.stringify(cache));
  } catch (error) {
    console.error('[useDailyInsights] Error saving cache:', error);
  }
}

/**
 * React hook for managing daily insights
 */
export function useDailyInsights(viewAll: boolean = false): UseDailyInsightsState {
  const { data: session } = useSession();
  const orgId = session?.user?.activeOrganizationId ?? null;
  const [hasSeenToday, setHasSeenToday] = useState(false);
  const [localInsight, setLocalInsight] = useState<DailyInsight | null>(null);

  // Read the localStorage cache exactly once per mount — seeds React Query's
  // initialData/initialDataUpdatedAt below so it becomes the single staleness
  // authority (see GitHub #642) instead of a second, independent TTL clock.
  const initialCacheRef = useRef<InsightsCache | null | undefined>(undefined);
  if (initialCacheRef.current === undefined) {
    initialCacheRef.current = !viewAll ? loadCache(orgId) : null;
  }

  // Hydrate 'seen' from cache (React Query has no concept of it). Also purge
  // the stale 'hasEverHadInsights' key written by older code that didn't
  // verify insightId — it caused the Insights sidebar button to appear even
  // when there were no insights.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STALE_HAS_INSIGHTS_KEY); // one-time migration
    }
    if (initialCacheRef.current) {
      setHasSeenToday(initialCacheRef.current.seen);
    }
  }, []);

  // Fetch today's insights with React Query
  const {
    data: todayInsight,
    isLoading: isLoadingToday,
    error: todayError,
    refetch,
  } = useQuery({
    queryKey: dailyInsightsTodayQueryKey(session?.user?.id, orgId, viewAll),
    queryFn: async () => {
      if (!session) {
        return null; // Return null instead of throwing
      }

      try {
        // Fetch from API (no session needed - handled by API route)
        const client = createInsightsClient();
        const insight = await client.getTodayInsights(viewAll);

        // Only persist a finished insight to cache — a 'generating' placeholder
        // must keep being re-fetched (via refetchInterval below) until it's ready.
        // This is what seeds initialData on the *next* full page reload; React
        // Query's own staleTime (below) governs freshness within this session.
        if (!viewAll && insight && insight.status !== 'generating') {
          saveCache(orgId, insight, false);
        }

        return insight;
      } catch (error) {
        console.error('[useDailyInsights] Failed to fetch insights, returning null:', error);
        return null; // Return null on error instead of throwing
      }
    },
    enabled: !!session,
    staleTime: STALE_TIME.STANDARD,
    // Survive a full page reload: seed from the localStorage cache read once
    // above so there's no loading flash, while staleTime (not the cache's own
    // TTL) decides whether a background refetch happens.
    initialData: () => initialCacheRef.current?.insight ?? undefined,
    initialDataUpdatedAt: () => initialCacheRef.current?.timestamp,
    retry: false, // Don't retry on failure to prevent infinite loops
    refetchInterval: (query) => {
      // Never poll while the query is in error state — the backend is likely
      // unreachable and repeating requests would pile up timeouts (same
      // reasoning as lib/hooks/useOnboarding.ts).
      if (query.state.error) return false;
      const data = query.state.data as DailyInsight | null | undefined;
      return data?.status === 'generating' ? 4000 : false;
    },
  });

  // Sync local state with every finished query result. Skip 'generating'
  // snapshots — localInsight should only ever hold a finished insight (or
  // null while one is still being generated); refetchInterval above keeps
  // polling until it is, so a 'generating' response here would otherwise
  // flash the UI back to a placeholder mid-poll.
  //
  // This used to only fire once (guarded on `!localInsight`), which meant a
  // later, corrected refetch — e.g. after a backend data fix, or the
  // explicit "refresh" action — could never reach the UI within the same
  // session: the first snapshot loaded (including one seeded from a stale,
  // up-to-24h localStorage cache via initialData) stuck around until the
  // user switched org/viewAll or reloaded into a fresh cache. Always syncing
  // to the latest finished result fixes that while still never regressing
  // to a blank/'generating' state.
  //
  // The "clear on scope change" behavior used to live in a second, separate
  // effect keyed on [viewAll, orgId]. When toggling scope AND React Query
  // already had cached/ready data for the new scope's query key (common —
  // e.g. switching back to a scope fetched earlier this session), both
  // effects fired in the same commit: this one set the real data, then the
  // second one unconditionally nulled it right after, with nothing left to
  // re-fire and repair it since `todayInsight` had already settled. That
  // permanently stuck the card on "No insight generated for today yet." for
  // whichever scope was toggled to (GitHub #184 follow-up). Merged into one
  // effect so "is there ready data for the new scope" is always checked
  // before deciding to clear, never after.
  const scopeKeyRef = useRef(`${orgId ?? ''}:${viewAll}`);
  useEffect(() => {
    const scopeKey = `${orgId ?? ''}:${viewAll}`;
    const scopeChanged = scopeKeyRef.current !== scopeKey;
    scopeKeyRef.current = scopeKey;

    if (todayInsight && todayInsight.status !== 'generating') {
      setLocalInsight(todayInsight);
    } else if (scopeChanged) {
      setLocalInsight(null);
    }
  }, [todayInsight, viewAll, orgId]);

  // Lightweight query: check whether ANY historical insight exists.
  // Uses limit=1 so the payload is minimal. Cached for 15 minutes.
  // This is the ONLY reliable signal for showing the Insights sidebar item.
  const { data: hasHistoricalInsights = false } = useQuery({
    queryKey: insightsHasAnyQueryKey(session?.user?.id, orgId),
    queryFn: async () => {
      if (!session) return false;
      try {
        const client = createInsightsClient();
        const history = await client.getInsightsHistory(1, 0);
        return Array.isArray(history) && history.length > 0;
      } catch {
        return false;
      }
    },
    enabled: !!session,
    // Intentionally outside the STALE_TIME.FAST/STANDARD tiers (GitHub #638): this is a
    // lightweight limit=1 existence check for "any historical insights at all", not a
    // freshness-sensitive read, so a longer staleTime is deliberate.
    staleTime: 15 * 60 * 1000,
    retry: false,
  });

  /**
   * Mark today's insight as seen
   */
  const markAsSeen = useCallback(() => {
    setHasSeenToday(true);

    if (localInsight) {
      saveCache(orgId, localInsight, true);

      // Mark as viewed in localStorage
      const id = localInsight.insightId || localInsight.id;
      if (id) {
        markInsightAsViewed(id, localInsight.dateKey);
        
        // Optionally sync with backend (non-blocking, will fail silently)
        const client = createInsightsClient();
        client.markInsightAsViewed(id, localInsight.dateKey).catch(error => {
          console.debug('[useDailyInsights] Backend sync skipped:', error);
        });
      }
    }
  }, [localInsight, orgId]);

  /**
   * Manually refresh insights
   */
  const refreshInsights = useCallback(async () => {
    await refetch();
  }, [refetch]);

  /**
   * Determine if badge should be shown
   */
  const shouldShowBadge = !!(localInsight?.insightId && !hasSeenToday);

  /**
   * True when at least one real insight exists (today's cached insight OR any
   * historical insight confirmed by the API). Drives the Insights sidebar item.
   */
  const hasInsights = !!(localInsight?.insightId || hasHistoricalInsights);

  /**
   * True while today's insight is generating in the background (async
   * generation — see backend get_or_generate_today_insights). Driven off the
   * raw query data, not localInsight, since localInsight is deliberately
   * never set to a 'generating' snapshot.
   */
  const isGenerating = todayInsight?.status === 'generating';

  return {
    todayInsight: localInsight,
    isLoadingToday,
    todayError: todayError as Error | null,
    isGenerating,
    hasSeenToday,
    shouldShowBadge,
    hasInsights,
    markAsSeen,
    refreshInsights,
  };
}
